import React, { useEffect, useState } from 'react';
import { Edit3Icon, PaintBucketIcon, CopyIcon, TrashIcon, MaximizeIcon, RotateCwIcon, StarIcon, LockIcon, UnlockIcon, DropletIcon, GlassWaterIcon, CircleIcon, ChevronRightIcon, XIcon, BoxIcon, MessageSquareIcon, ClipboardIcon, GroupIcon, SlidersIcon } from 'lucide-react';
interface RadialMenuProps {
  position: {
    x: number;
    y: number;
  };
  onClose: () => void;
  onAction: (action: string, subAction?: string) => void;
  objectType?: string;
  onShowProperties?: () => void;
}
export function RadialMenu({
  position,
  onClose,
  onAction,
  objectType = 'cabinet',
  onShowProperties
}: RadialMenuProps) {
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const [hoveredAction, setHoveredAction] = useState<string | null>(null);
  const [noteNumber, setNoteNumber] = useState(1);
  const [showSidePanel, setShowSidePanel] = useState(false);
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.radial-menu') && !target.closest('.side-panel')) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);
  // Main menu actions - Modified to highlight the Edit Properties (now using SlidersIcon)
  const mainActions = [{
    id: 'edit',
    label: 'Edit Properties',
    icon: SlidersIcon,
    primary: true // Mark as primary action
  }, {
    id: 'material',
    label: 'Material/Color',
    icon: PaintBucketIcon,
    hasSubmenu: true
  }, {
    id: 'duplicate',
    label: 'Duplicate',
    icon: CopyIcon
  }, {
    id: 'delete',
    label: 'Delete',
    icon: TrashIcon
  }, {
    id: 'resize',
    label: 'Resize',
    icon: MaximizeIcon
  }, {
    id: 'rotate',
    label: 'Rotate',
    icon: RotateCwIcon
  }, {
    id: 'note',
    label: `Add Note #${noteNumber}`,
    icon: MessageSquareIcon
  }, {
    id: 'copyStyle',
    label: 'Copy Style',
    icon: ClipboardIcon
  }, {
    id: 'group',
    label: 'Group Items',
    icon: GroupIcon
  }, {
    id: 'favorite',
    label: 'Add to Favorites',
    icon: StarIcon
  }, {
    id: 'lock',
    label: 'Lock/Unlock',
    icon: LockIcon
  }];
  // Submenu actions for material
  const materialSubmenu = [{
    id: 'wood',
    label: 'Wood',
    icon: BoxIcon
  }, {
    id: 'lacquer',
    label: 'Lacquer',
    icon: DropletIcon
  }, {
    id: 'glass',
    label: 'Glass',
    icon: GlassWaterIcon
  }, {
    id: 'metal',
    label: 'Metal',
    icon: CircleIcon
  }, {
    id: 'more',
    label: 'More...',
    icon: ChevronRightIcon
  }];
  // Calculate positions in a circle
  const getPosition = (index: number, total: number, radius: number = 120) => {
    // Start from the top (negative y is up in screen coordinates)
    const angle = index / total * 2 * Math.PI - Math.PI / 2;
    const x = radius * Math.cos(angle);
    const y = radius * Math.sin(angle);
    return {
      x,
      y
    };
  };
  const handleAction = (actionId: string) => {
    if (actionId === 'material') {
      setActiveSubmenu('material');
      setShowSidePanel(true);
    } else if (actionId === 'note') {
      onAction(actionId, noteNumber.toString());
      setNoteNumber(prev => prev + 1);
      onClose();
    } else if (actionId === 'edit') {
      // Handle edit properties action - this will open the properties panel
      if (onShowProperties) {
        onShowProperties();
      } else {
        onAction('editProperties');
      }
      onClose();
    } else {
      onAction(actionId);
      onClose();
    }
  };
  const handleSubmenuAction = (actionId: string) => {
    onAction('material', actionId);
    onClose();
  };
  return <>
      <div className="radial-menu fixed z-50" style={{
      left: position.x,
      top: position.y
    }} data-id="element-704">
        {/* Center close button */}
        <button className="absolute z-40 bg-white shadow-lg rounded-full flex items-center justify-center w-12 h-12 border border-gray-200 hover:bg-gray-50 transform -translate-x-1/2 -translate-y-1/2 dark:bg-darkbg-secondary dark:border-darkbg-border dark:hover:bg-darkbg-primary" onClick={onClose} data-id="element-705">
          <XIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" data-id="element-706" />
        </button>
        {/* Main radial menu */}
        {mainActions.map((action, index) => {
        const pos = getPosition(index, mainActions.length);
        const isHovered = hoveredAction === action.id;
        return <div key={action.id} className="absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-150" style={{
          left: `calc(50% + ${pos.x}px)`,
          top: `calc(50% + ${pos.y}px)`,
          zIndex: isHovered ? 30 : 20
        }} onMouseEnter={() => setHoveredAction(action.id)} onMouseLeave={() => setHoveredAction(null)} data-id="element-707">
              <button className={`${action.primary ? 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-accent-blue dark:hover:bg-blue-500' : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-darkbg-secondary dark:text-gray-300 dark:hover:bg-darkbg-primary'} shadow-lg rounded-full flex items-center justify-center transition-all duration-150 border ${action.primary ? 'border-blue-600 dark:border-accent-blue' : 'border-gray-200 dark:border-darkbg-border'} ${isHovered ? 'w-14 h-14 scale-110' : 'w-12 h-12'}`} onClick={() => handleAction(action.id)} title={action.label} data-id="element-708">
                <action.icon className="h-5 w-5" data-id="element-709" />
              </button>
              {isHovered && <div className="absolute bg-white text-gray-800 px-2 py-1 rounded shadow-md text-xs font-medium whitespace-nowrap transform -translate-x-1/2 mt-1 border border-gray-200 dark:bg-darkbg-secondary dark:text-gray-300 dark:border-darkbg-border" data-id="element-710">
                  {action.label}
                </div>}
            </div>;
      })}
        {/* Material submenu */}
        {activeSubmenu === 'material' && <div className="absolute inset-0 pointer-events-none" data-id="element-711">
            {materialSubmenu.map((action, index) => {
          const pos = getPosition(index, materialSubmenu.length, 190);
          const isHovered = hoveredAction === `sub-${action.id}`;
          return <div key={action.id} className="absolute transform -translate-x-1/2 -translate-y-1/2 pointer-events-auto" style={{
            left: `calc(50% + ${pos.x}px)`,
            top: `calc(50% + ${pos.y}px)`,
            zIndex: isHovered ? 30 : 20
          }} onMouseEnter={() => setHoveredAction(`sub-${action.id}`)} onMouseLeave={() => setHoveredAction(null)} data-id="element-712">
                  <button className={`bg-white text-gray-700 shadow-lg rounded-full flex items-center justify-center transition-all duration-150 border border-gray-200 ${isHovered ? 'w-12 h-12 scale-110' : 'w-10 h-10'} dark:bg-darkbg-secondary dark:border-darkbg-border dark:text-gray-300 dark:hover:bg-darkbg-primary`} onClick={() => handleSubmenuAction(action.id)} title={action.label} data-id="element-713">
                    <action.icon className="h-4 w-4" data-id="element-714" />
                  </button>
                  {isHovered && <div className="absolute bg-white text-gray-800 px-2 py-1 rounded shadow-md text-xs font-medium whitespace-nowrap transform -translate-x-1/2 mt-1 border border-gray-200 dark:bg-darkbg-secondary dark:text-gray-300 dark:border-darkbg-border" data-id="element-715">
                      {action.label}
                    </div>}
                </div>;
        })}
          </div>}
      </div>
      {/* Side panel for extended options */}
      {showSidePanel && <div className="side-panel fixed z-40 right-0 top-0 h-full w-80 bg-white shadow-lg border-l border-gray-200 dark:bg-darkbg-secondary dark:border-darkbg-border overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-716">
          <div className="p-4 border-b border-gray-200 dark:border-darkbg-border flex justify-between items-center" data-id="element-717">
            <h3 className="font-medium text-gray-800 dark:text-gray-200" data-id="element-718">
              {activeSubmenu === 'material' ? 'Materials & Colors' : 'Options'}
            </h3>
            <button className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-darkbg-primary" onClick={() => {
          setShowSidePanel(false);
          setActiveSubmenu(null);
        }} data-id="element-719">
              <XIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" data-id="element-720" />
            </button>
          </div>
          {activeSubmenu === 'material' && <div className="p-4" data-id="element-721">
              <div className="mb-4" data-id="element-722">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-723">
                  Wood
                </h4>
                <div className="grid grid-cols-2 gap-3" data-id="element-724">
                  {['Oak', 'Walnut', 'Maple', 'Cherry', 'Birch', 'Mahogany'].map(wood => <button key={wood} className="p-2 border border-gray-200 dark:border-darkbg-border rounded-md hover:bg-gray-50 dark:hover:bg-darkbg-primary flex flex-col items-center" onClick={() => handleSubmenuAction(wood.toLowerCase())} data-id="element-725">
                      <div className={`w-full h-16 rounded-md mb-1 ${wood === 'Oak' ? 'bg-amber-200' : wood === 'Walnut' ? 'bg-amber-800' : wood === 'Maple' ? 'bg-amber-100' : wood === 'Cherry' ? 'bg-red-800' : wood === 'Birch' ? 'bg-yellow-100' : 'bg-amber-900'}`} data-id="element-726"></div>
                      <span className="text-xs text-gray-700 dark:text-gray-300" data-id="element-727">
                        {wood}
                      </span>
                    </button>)}
                </div>
              </div>
              <div className="mb-4" data-id="element-728">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-729">
                  Lacquer
                </h4>
                <div className="grid grid-cols-2 gap-3" data-id="element-730">
                  {['White', 'Black', 'Gray', 'Blue', 'Red', 'Green'].map(color => <button key={color} className="p-2 border border-gray-200 dark:border-darkbg-border rounded-md hover:bg-gray-50 dark:hover:bg-darkbg-primary flex flex-col items-center" onClick={() => handleSubmenuAction(`${color.toLowerCase()}-lacquer`)} data-id="element-731">
                        <div className={`w-full h-16 rounded-md mb-1 ${color === 'White' ? 'bg-white border border-gray-200' : color === 'Black' ? 'bg-gray-900' : color === 'Gray' ? 'bg-gray-400' : color === 'Blue' ? 'bg-blue-500' : color === 'Red' ? 'bg-red-500' : 'bg-green-500'}`} data-id="element-732"></div>
                        <span className="text-xs text-gray-700 dark:text-gray-300" data-id="element-733">
                          {color}
                        </span>
                      </button>)}
                </div>
              </div>
              <div className="mb-4" data-id="element-734">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-735">
                  Glass
                </h4>
                <div className="grid grid-cols-2 gap-3" data-id="element-736">
                  {['Clear', 'Frosted', 'Tinted', 'Patterned'].map(glass => <button key={glass} className="p-2 border border-gray-200 dark:border-darkbg-border rounded-md hover:bg-gray-50 dark:hover:bg-darkbg-primary flex flex-col items-center" onClick={() => handleSubmenuAction(`${glass.toLowerCase()}-glass`)} data-id="element-737">
                      <div className={`w-full h-16 rounded-md mb-1 ${glass === 'Clear' ? 'bg-sky-50 border border-gray-200' : glass === 'Frosted' ? 'bg-gray-100 border border-gray-200' : glass === 'Tinted' ? 'bg-blue-200 border border-gray-200' : 'bg-gray-200 border border-gray-300'}`} data-id="element-738"></div>
                      <span className="text-xs text-gray-700 dark:text-gray-300" data-id="element-739">
                        {glass}
                      </span>
                    </button>)}
                </div>
              </div>
              <div className="mb-4" data-id="element-740">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-741">
                  Metal
                </h4>
                <div className="grid grid-cols-2 gap-3" data-id="element-742">
                  {['Stainless', 'Brass', 'Copper', 'Bronze'].map(metal => <button key={metal} className="p-2 border border-gray-200 dark:border-darkbg-border rounded-md hover:bg-gray-50 dark:hover:bg-darkbg-primary flex flex-col items-center" onClick={() => handleSubmenuAction(`${metal.toLowerCase()}-metal`)} data-id="element-743">
                      <div className={`w-full h-16 rounded-md mb-1 ${metal === 'Stainless' ? 'bg-gray-300' : metal === 'Brass' ? 'bg-yellow-700' : metal === 'Copper' ? 'bg-amber-600' : 'bg-amber-800'}`} data-id="element-744"></div>
                      <span className="text-xs text-gray-700 dark:text-gray-300" data-id="element-745">
                        {metal}
                      </span>
                    </button>)}
                </div>
              </div>
              <div className="pt-3 border-t border-gray-200 dark:border-darkbg-border" data-id="element-746">
                <button onClick={() => {
            setShowSidePanel(false);
            setActiveSubmenu(null);
          }} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-md text-sm font-medium dark:bg-accent-blue dark:hover:bg-blue-500" data-id="element-747">
                  Apply Selection
                </button>
              </div>
            </div>}
        </div>}
    </>;
}